import { supabase } from './supabase';
import type { Country } from './localData';

// Suggestion only — every extracted row is shown to the user for review and
// correction before it can be saved, per docs/AI_RULES.md. The preferred
// cloud path uploads the original once and sends only its private document id
// to the server. Large PDFs are split into page batches by process-lab-document.
export interface ExtractedLabItem {
  // Exact text from the report; never translated or normalized.
  analyte: string;
  russianName?: string;
  internationalName?: string;
  canonicalCode?: string;
  aliases?: string[];
  // Exactly one of value/valueText is set — see LabResult in localData.ts.
  value?: number;
  valueText?: string;
  unit?: string;
  referenceLow?: number;
  referenceHigh?: number;
  referenceText?: string;
  panel?: string;
  // Assigned by the durable processor from the physical page and the
  // top-to-bottom source order. These stay hidden in the review UI but make
  // re-confirming the exact same source row safe and idempotent.
  sourcePage?: number;
  sourceRow?: number;
  sourceSection?: string;
}

export interface MetadataCandidate {
  value: string;
  sourcePages: number[];
}

export interface DocumentMetadataOptions {
  takenOn?: MetadataCandidate[];
  labName?: MetadataCandidate[];
  countryGuess?: MetadataCandidate[];
}

export interface LabExtraction {
  takenOn?: string;
  labName?: string;
  countryGuess?: Country;
  metadataOptions?: DocumentMetadataOptions;
  note?: string;
  laboratoryConclusion?: string;
  laboratoryConclusionPage?: number;
  items: ExtractedLabItem[];
  jobId?: string;
  pageCount?: number;
  batchCount?: number;
  processorVersion?: string;
  processing?: boolean;
}

export interface DocumentProcessingProgress {
  status: 'queued' | 'processing' | 'review' | 'completed' | 'failed' | 'cancelled';
  completedBatches: number;
  totalBatches: number;
}

type ErrorResponse = { clone: () => { json: () => Promise<unknown> } };
type ProcessingJob = {
  status: 'queued' | 'processing' | 'review' | 'completed' | 'failed' | 'cancelled';
  completed_batches?: number | null;
  total_batches?: number | null;
  error_message?: string | null;
  result_summary?: { extraction?: LabExtraction } | null;
};

type StoredReviewJob = {
  id: string;
  total_batches?: number | null;
  result_summary?: { extraction?: LabExtraction } | null;
};

const JOB_POLL_INTERVAL_MS = 5_000;
const MAX_DOCUMENT_JOB_POLLS = 40;

function isErrorResponse(value: unknown): value is ErrorResponse {
  return value !== null
    && typeof value === 'object'
    && 'clone' in value
    && typeof value.clone === 'function';
}

async function invokeExtraction(functionName: string, body: Record<string, unknown>): Promise<LabExtraction> {
  if (!supabase) throw new Error('Supabase не настроен');
  const { data, error } = await supabase.functions.invoke(functionName, { body });
  if (error) {
    let message = error.message;
    // Supabase may return a Response created in another JavaScript realm
    // (notably mobile Safari), where `instanceof Response` is false. Read a
    // response-shaped context instead so the user sees the server's safe
    // message rather than the generic non-2xx SDK text.
    if ('context' in error && isErrorResponse(error.context)) {
      try {
        const responseBody = await error.context.clone().json();
        if (responseBody && typeof responseBody === 'object' && 'error' in responseBody
          && typeof responseBody.error === 'string') message = responseBody.error;
      } catch {
        // Ignore malformed error bodies and keep the Supabase message.
      }
    }
    throw new Error(message);
  }
  return data as LabExtraction;
}

async function readDocumentProcessingJob(jobId: string): Promise<ProcessingJob> {
  if (!supabase) throw new Error('Supabase не настроен');
  const { data, error } = await supabase
    .from('document_processing_jobs')
    .select('status, completed_batches, total_batches, error_message, result_summary')
    .eq('id', jobId)
    .single();
  if (error || !data) throw new Error('Не удалось проверить состояние обработки документа');
  return data as ProcessingJob;
}

// A completed AI result is a suggestion awaiting the user's review. Reading
// that durable payload must not start a new processor job or send the source
// PDF to the provider again. This also keeps review available for reports
// processed by an earlier, compatible processor version.
export async function loadLabDocumentReview(documentId: string): Promise<LabExtraction> {
  if (!documentId) throw new Error('Не указан документ для проверки');
  if (!supabase) throw new Error('Supabase не настроен');
  const { data, error } = await supabase
    .from('document_processing_jobs')
    .select('id, total_batches, result_summary')
    .eq('document_id', documentId)
    .in('status', ['review', 'completed'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error('Не удалось открыть результат распознавания');

  const job = data as StoredReviewJob | null;
  const extraction = job?.result_summary?.extraction;
  if (!job || !extraction || !Array.isArray(extraction.items)) {
    throw new Error('Для этого документа нет результата, готового к проверке.');
  }
  return {
    ...extraction,
    jobId: job.id,
    batchCount: typeof job.total_batches === 'number' ? job.total_batches : extraction.batchCount,
  };
}

export async function processLabDocument(
  documentId: string,
  model: string,
  onProgress?: (progress: DocumentProcessingProgress) => void,
): Promise<LabExtraction> {
  if (!documentId) throw new Error('Не указан документ для распознавания');
  // One invocation starts a durable background claim. Poll the protected job
  // row, not the Edge Function, so waiting never creates duplicate requests.
  for (let pageAttempt = 0; pageAttempt < 20; pageAttempt += 1) {
    const extraction = await invokeExtraction('process-lab-document', { documentId, model });
    if (!extraction.processing) return extraction;
    if (!extraction.jobId) throw new Error('Сервер не вернул идентификатор задачи обработки');
    onProgress?.({
      status: 'queued',
      completedBatches: 0,
      totalBatches: extraction.batchCount || extraction.pageCount || 1,
    });
    for (let poll = 0; poll < MAX_DOCUMENT_JOB_POLLS; poll += 1) {
      await new Promise(resolve => setTimeout(resolve, JOB_POLL_INTERVAL_MS));
      const job = await readDocumentProcessingJob(extraction.jobId);
      onProgress?.({
        status: job.status,
        completedBatches: Number.isFinite(job.completed_batches) ? Math.max(0, Number(job.completed_batches)) : 0,
        totalBatches: Number.isFinite(job.total_batches) ? Math.max(1, Number(job.total_batches)) : (extraction.batchCount || extraction.pageCount || 1),
      });
      const completed = job.result_summary?.extraction;
      if ((job.status === 'review' || job.status === 'completed') && completed) return completed;
      if (job.status === 'failed' || job.status === 'cancelled') {
        throw new Error(job.error_message || 'Не удалось обработать документ');
      }
      // A page completed. Start the next durable page claim in a new request.
      if (job.status === 'queued') break;
    }
  }
  throw new Error('Обработка документа занимает больше ожидаемого времени. Готовые страницы сохранятся; повторите проверку позже.');
}

// Legacy data-URL path remains for local mode and very small files only.
export async function analyzeLabReport(documentDataUrl: string, model: string): Promise<LabExtraction> {
  return invokeExtraction('analyze-lab-report', { document: documentDataUrl, model });
}
